import React, { Fragment, useCallback } from "react";
import { Text, View, Pressable } from "react-native";
import { Button } from "@/elements/components/ui/button";
import { useFormatDate } from "@/elements/hooks/use-format-date";
import { Avatar } from "../ui/avatar";
import RemoteIcon from "@/assets/svgs/remote-icon";
import UserIcon from "@/assets/svgs/user-icon";
import ClockIcon from "@/assets/svgs/clock-icon";
import { api } from "../utils/convex-api";
import { router } from "expo-router";
import {
  TutorLessonSheetPayload,
  TutorLessonSheetResult,
} from "../sheets/tutor-lesson-sheet";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { setNavigationData } from "@/elements/hooks/use-navigation-data";
import { Id } from "@teachloop/backend/_generated/dataModel";

function TutorSessionCard({
  session,
  open,
  openCancelLessonScreen,
}: {
  session: NonNullable<typeof api.tutor.home.queries.getSessions._returnType>[0];
  open: (payload: TutorLessonSheetPayload) => Promise<TutorLessonSheetResult>;
  openCancelLessonScreen: (payload: { id: Id<"sessions"> }) => void;
}) {
  const fmt = useFormatDate();
  const isSessionInProgress = session.status === "in_progress";
  const isSessionCompleted = session.status === "completed";
  const isSessionNoShow = session.status === "no_show_reported_by_student" ||
    session.status === "no_show_reported_by_tutor";

  const { mutateAsync: setSessionInProgress } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.home.mutations.setSessionInProgress
    ),
  });

  const handleOpenLessonSheet = useCallback(async () => {
    const result = await open({
      lessonData: session,
    });
    if (result?.joinLesson) {
      if (session.status === "scheduled") {
        await setSessionInProgress({ sessionId: session.sessionId });
      }
      if (!session.url) throw new Error("session url expected");
      location.href = session.url;
    } else if (result?.cancelLesson) {
      openCancelLessonScreen({
        id: session.sessionId,
      });
    } else if (result?.reportNoShow) {
      router.push(`/(app)/report-tutor-no-show?lessonId=${session.sessionId}`);
    } else if (result?.reportStudentNoShow) {
      router.push(`/report-student-no-show?lessonId=${session.sessionId}`);
    }
  }, [open, session, setSessionInProgress, openCancelLessonScreen]);

  const onStartLessonSheetOrModal = () => {
    if (session.deliveryMethod === "remote") {
      handleOpenLessonSheet();
    } else {
      setNavigationData("/lesson-recording/start-in-person-lesson", session);
      router.push("/(app)/lesson-recording/start-in-person-lesson");
    }
  };

  return (
    <Fragment>
      <Pressable
        onPress={onStartLessonSheetOrModal}
        className={`p-4 rounded-[17px] gap-3 shadow-hard-3 ${
          isSessionInProgress
            ? "bg-teachLoop-accentPrimary"
            : isSessionCompleted
              ? "bg-teachLoop-accentPrimaryDisabled"
              : "bg-[#FFFDFB]"
        }`}
        style={{
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.2,
          shadowRadius: 2,
          elevation: 2,
        }}
        disabled={false}
      >
        {isSessionNoShow && (
          <View className="mb-2">
            <Text className="font-geologica-medium font-medium text-sm text-red-600">
              No Show
            </Text>
          </View>
        )}
        <View className="flex-row items-center justify-between">
          <View className="flex-row items-center gap-2">
            <Avatar
              size="sm"
              className={`h-8 w-8 ${isSessionCompleted ? "bg-teachLoop-creamLight" : ""}`}
            >
              <Avatar.Content
                label={session.student.name}
                labelSize="sm"
                size="sm"
                source={
                  session.student.imageUrl
                    ? { uri: session.student.imageUrl }
                    : undefined
                }
              />
            </Avatar>
            <Text
              className={`font-geologica-medium font-medium text-base ${
                isSessionInProgress ? "text-white" : "text-gray-800"
              } leading-[1.5]`}
            >
              {session.student.name}
            </Text>
          </View>
          <Fragment>
            {isSessionInProgress && session.url ? (
              <Button
                className="bg-white"
                size="sm"
                icon
                onPress={onStartLessonSheetOrModal}
              >
                <View className="w-[9px] h-[9px] bg-green-400 rounded-full items-center justify-center" />
                <Button.Text
                  intent="primary"
                  size="sm"
                  className="hover:text-white"
                >
                  {session.deliveryMethod === "remote"
                    ? "Join lesson"
                    : "Start lesson"}
                </Button.Text>
              </Button>
            ) : (
              <Text className={`font-geologica-medium font-medium text-base ${isSessionInProgress ? "text-white" : "text-gray-800"} leading-[1.2]`}>
                {fmt(session.startTime, "do MMMM")}
              </Text>
            )}
          </Fragment>
        </View>

        <View className="flex-row items-center justify-between">
          <Text
            className={`font-geologica-regular font-normal ${
              isSessionInProgress ? "text-white" : "text-gray-800"
            } text-sm leading-[1.5]`}
          >
            {session.subjectName}
          </Text>

          <View className="flex-row items-center gap-[6px]">
            {session.deliveryMethod === "in_person" ? (
              <UserIcon
                className="size-4"
                pathClassName={
                  isSessionInProgress ? "stroke-white" : "stroke-gray-800"
                }
              />
            ) : (
              <RemoteIcon
                className="size-4"
                pathClassName={
                  isSessionInProgress ? "stroke-white" : "stroke-gray-800"
                }
              />
            )}
            <Text
              className={`font-geologica-regular font-normal ${
                isSessionInProgress ? "text-white" : "text-gray-800"
              } text-sm leading-[1.5]`}
            >
              {session.deliveryMethod === "in_person" ? "In person" : "Remote"}
            </Text>
          </View>

          <View className="flex-row items-center gap-2">
            <ClockIcon
              className="size-4"
              pathClassName={
                isSessionInProgress ? "stroke-white" : "stroke-gray-800"
              }
            />
            <Text
              className={`font-geologica-regular font-normal ${
                isSessionInProgress ? "text-white" : "text-gray-800"
              } text-sm leading-[1.5]`}
            >
              {fmt(session.startTime, "HH:mm")} -{" "}
              {fmt(session.endTime, "HH:mm")}
            </Text>
          </View>
        </View>
      </Pressable>
    </Fragment>
  );
}

export default TutorSessionCard;
